/* eslint-disable guard-for-in */
/* eslint-disable no-restricted-syntax */
import { createSelector } from 'reselect';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import remove from 'lodash/remove';
import { sortBy } from 'lodash';
import { initialState } from './reducer';
import { createSelectFieldOptions, separateExcludedIncludedCountries } from '../../utils/common';
import { SINGER_PROFESSION_TYPE_ID } from '../../constants/consts';
import { AGREEMENTS_TYPES, AGREEMENTS_CASES } from './constants';

const selectAgenciesDomain = state => state.Agencies || initialState;

const selectAgencyInfo = () => createSelector(selectAgenciesDomain, subState => get(subState, 'agencyInfo'));
const selectAgencyContacts = () =>
  createSelector(selectAgenciesDomain, subState => get(subState, 'agencyContact.data', []));

const selectOffices = () => createSelector(selectAgenciesDomain, subState => get(subState, 'offices'));

const selectArtists = () =>
  createSelector(selectAgenciesDomain, subState => {
    const artists = get(subState, 'artists') || [];
    const agreements = get(subState, 'agreements', []);
    const sortSectionByName = section => {
      const updatedSection = {};
      for (const key in section) {
        updatedSection[key] = sortBy(section[key], 'artist.profile.lastName');
      }
      return updatedSection;
    };

    // set agreements
    const groupedAgreements = groupBy(agreements, 'connection.id');
    artists.forEach(artist => {
      const updatedArtist = artist;
      const artistAgreements = groupedAgreements[get(artist, 'id')] || [];
      remove(artistAgreements, { agreementType: AGREEMENTS_TYPES.BOTH }); // for now we don`t show such agreements

      updatedArtist.agreements = artistAgreements.map(agreement => {
        const { included, excluded } = separateExcludedIncludedCountries([
          ...get(agreement, 'countries', []),
          ...get(agreement, 'regions', []),
        ]);
        return {
          ...agreement,
          includedCountries: included,
          excludedCountries: excluded,
        };
      });
    });
    // group artists
    const groupedProfessions = groupBy(artists, 'artist.profile.profession.id');
    const singers = groupedProfessions[SINGER_PROFESSION_TYPE_ID];
    const voiceTypes = groupBy(singers, 'artist.profile.profession.professionQualification.id');
    delete groupedProfessions[SINGER_PROFESSION_TYPE_ID];
    let undefinedProfession = [];
    if (voiceTypes.undefined) {
      undefinedProfession = undefinedProfession.concat(voiceTypes.undefined);
      delete voiceTypes.undefined;
    }
    if (groupedProfessions.undefined) {
      undefinedProfession = undefinedProfession.concat(groupedProfessions.undefined);
      delete groupedProfessions.undefined;
    }
    const undefinedProfessionToSet =
      undefinedProfession.length > 0 ? { 'profession not specified': undefinedProfession } : {};
    return {
      voices: sortSectionByName(voiceTypes),
      professions: sortSectionByName(groupedProfessions),
      notSpecified: sortSectionByName(undefinedProfessionToSet),
    };
  });

const selectAgreements = () => createSelector(selectAgenciesDomain, subState => get(subState, 'agreements'));

const selectAgencyCountries = () =>
  createSelector(selectAgenciesDomain, subState => get(subState, 'agencyCountries.data', []));

const selectAgencyAgents = () => createSelector(selectAgenciesDomain, subState => get(subState, 'agencyAgents'));
const selectAgencyAgentsOptions = () =>
  createSelector(selectAgenciesDomain, subState =>
    createSelectFieldOptions(get(subState, 'agencyAgents'), 'id', 'agent.profile.name', {}, true),
  );

const selectAgencyCountriesOptions = () =>
  createSelector(selectAgenciesDomain, subState =>
    createSelectFieldOptions(get(subState, 'agencyCountries.data', []), 'id', 'name', {}, true),
  );

const selectUserProfileAgentInfo = () =>
  createSelector(selectAgenciesDomain, subState => get(subState, 'agentInfoOfUserProfile'));

const selectAddedToRosterArtist = () =>
  createSelector(selectAgenciesDomain, subState => get(subState, 'addedToRosterArtist'));

const selectIfArtistInRoster = () =>
  createSelector(selectAgenciesDomain, subState => get(subState, 'artistInRosterData'));

const selectSingleArtistInfo = () =>
  createSelector(selectAgenciesDomain, subState => {
    const artistInfo = get(subState, 'artistInfo');
    const agreements = get(artistInfo, 'agreements', []);
    const getCountriesOptions = agreement => {
      const regions = get(agreement, 'regions', []);
      const countries = get(agreement, 'countries', []);
      const reagionsOptions = createSelectFieldOptions(regions, 'id', 'name', { type: 'region' });
      const countriesOptions = createSelectFieldOptions(countries, 'id', 'name', { type: 'country' });
      return [...reagionsOptions, ...countriesOptions];
    };

    const updatedAgreements = {};
    agreements.forEach(agr => {
      const updatedAgr = { ...agr };
      const type = get(agr, 'agreementType', '');
      const isExclusive = get(agr, 'exclusive', false);

      if (isExclusive) {
        const countries = separateExcludedIncludedCountries(get(agr, 'countries', []));
        const regions = separateExcludedIncludedCountries(get(agr, 'regions', []));

        updatedAgr.includedCountries = getCountriesOptions({
          countries: get(countries, 'included', []),
          regions: get(regions, 'included', []),
        });
        updatedAgr.excludedCountries = getCountriesOptions({
          countries: get(countries, 'excluded', []),
          regions: get(regions, 'excluded', []),
        });
      } else {
        updatedAgr.includedCountries = getCountriesOptions(agr);
      }

      switch (type) {
        case AGREEMENTS_TYPES.OPERA:
          updatedAgreements[
            isExclusive ? AGREEMENTS_CASES.OPERA_EXCLUSIVE : AGREEMENTS_CASES.OPERA_NON_EXCLUSIVE
          ] = updatedAgr;
          break;
        case AGREEMENTS_TYPES.CONCERT:
          updatedAgreements[
            isExclusive ? AGREEMENTS_CASES.CONCERT_EXCLUSIVE : AGREEMENTS_CASES.CONCERT_NON_EXCLUSIVE
          ] = updatedAgr;
          break;
        case AGREEMENTS_TYPES.GENERAL_MENEGMENT:
          updatedAgreements[AGREEMENTS_TYPES.GENERAL_MENEGMENT] = agr;
          break;
        default:
          break;
      }
    });
    return {
      ...artistInfo,
      agreements: updatedAgreements,
      contactPersons: get(agreements, '[0].contactPersons', []),
      agents: get(agreements, '[0].agents', []),
    };
  });

const selectArtistsSuggestinsList = () =>
  createSelector(selectAgenciesDomain, subState => {
    const artistsSuggestions = get(subState, 'artistsSuggestionsList');
    const artistsList = get(artistsSuggestions, 'artists', []);
    const artistsInAgency = get(artistsSuggestions, 'agencyArtists', []);
    const updatedArtists = artistsList.map(artist => {
      const artistData = artistsInAgency.find(ar => get(ar, 'artist.profile.id') === get(artist, 'id'));
      const isInAgencyRoster = Boolean(artistData);
      const additionalInfo = { isInRoster: isInAgencyRoster };
      if (isInAgencyRoster) {
        additionalInfo.connection_id = get(artistData, 'id');
      }
      return { ...artist, ...additionalInfo };
    });
    return updatedArtists;
  });

const selectArtistsSuggestinsListLoading = () =>
  createSelector(selectAgenciesDomain, subState => get(subState, 'artistsSuggestionsListLoading'));

const selectAgencyUpdating = () => createSelector(selectAgenciesDomain, subState => get(subState, 'agencyUpdating'));

export {
  selectAgencyInfo,
  selectOffices,
  selectArtists,
  selectAgreements,
  selectAgencyCountries,
  selectAgencyCountriesOptions,
  selectUserProfileAgentInfo,
  selectAddedToRosterArtist,
  selectIfArtistInRoster,
  selectSingleArtistInfo,
  selectAgencyAgents,
  selectAgencyAgentsOptions,
  selectArtistsSuggestinsList,
  selectArtistsSuggestinsListLoading,
  selectAgencyContacts,
  selectAgencyUpdating,
};
